import { useQuery } from 'react-query';
import { authFetch } from '../../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../../CommonUtils/urlWithParams';
import { OptionType } from '../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { whatIfsQueryKeys } from './queryKeys';
import { PensionPlanType } from '../components/WhatIf/ProfileChanges/PensionChange/PensionChange.meta';
import {
  BusinessSaleDTO,
  EarmarkValueDTO,
  MiscellaneousAssetSaleDTO,
  RealEstateSaleDTO,
} from './useWhatIfGroupsQuery';
import { IncomeOptionsDTO } from '../../../../ProfileOPL/Income/api/useIncomeQuery';

interface SaleOptions<Sale> {
  Assets: {
    AssetId: string;
    Label: string;
    SaleYears: { From: number; To: number };
    HasCurrentPlanSale: boolean;
  }[];
  Earmarks: {
    Label: string;
    Value: EarmarkValueDTO;
  }[];
}

export type BusinessSaleOptions = SaleOptions<BusinessSaleDTO>;
export type RealEstateSaleOptions = SaleOptions<RealEstateSaleDTO>;
export type MiscellaneousAssetSaleOptions = SaleOptions<MiscellaneousAssetSaleDTO>;

export enum IncomeType {
  EmploymentIncome = 1,
  BusinessIncome = 2,
  RealEstateIncome = 3,
  OtherIncome = 4,
}

export interface AnnuityPurchaseOptions {
  HasSpouse: boolean;
  YearsOptions: number[];
}

export interface WindfallOptions {
  EarmarkOptions: {
    Label: string;
    Value: EarmarkValueDTO;
  }[];
  YearOptions: number[];
  TaxStatusOptions: string[];
  OwnersOptions: { People: string[]; Trusts: string[] };
}

export interface PensionChangeOptions {
  PlanTypes: OptionType<PensionPlanType>[];
  PrincipalPensionAgeOptions: { Age: number; Year: number | null }[];
  SpousePensionAgeOptions: { Age: number; Year: number | null }[];
  PrincipalRetirementAge: number;
  SpouseRetirementAge: number | null;
}

type WhatIfOptions =
  | BusinessSaleOptions
  | RealEstateSaleOptions
  | MiscellaneousAssetSaleOptions
  | IncomeOptionsDTO
  | AnnuityPurchaseOptions
  | WindfallOptions
  | PensionChangeOptions;

interface FetchWhatIfOptionsParams {
  clientId: number;
  fpId: string;
  whatIfTypeName: string;
  goalId?: string;
}

const fetchWhatIfOptions = ({
  clientId,
  fpId,
  whatIfTypeName,
  goalId,
}: FetchWhatIfOptionsParams): Promise<WhatIfOptions> =>
  authFetch(
    urlWithParams(`/Consumer/WhatIf${whatIfTypeName}/Options`, {
      clientId,
      fpId,
      goalId,
    }),
  );

interface UseWhatIfOptionsQueryParams {
  clientId: number;
  fpId: string;
  whatIfTypeName: string;
  goalId?: string;
}

export const useWhatIfOptionsQuery = ({
  clientId,
  fpId,
  whatIfTypeName,
  goalId,
}: UseWhatIfOptionsQueryParams) => {
  return useQuery(
    whatIfsQueryKeys.options(clientId, fpId, whatIfTypeName, goalId),
    () => fetchWhatIfOptions({ clientId, fpId, whatIfTypeName, goalId }),
    { cacheTime: 0 },
  );
};
